
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import uuid from 'uuid';
import * as echarts from 'echarts';
import moment from 'moment';
import LineChartFilterable from '@/components/reports-v3/components/filterables/LineChartFilterable.vue';
import { COL, sqlExtractYear, sqlExtractMonth, sqlExtractDay, sqlSumByDocType, sqlSum, getTableName } from '@/worker/db/sqlHelpers';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import { PermissionsGroup } from '@/store/models.def';
import { GobiColor } from '@/helpers/color';
import gdbx from '@/store/modules/gdbx';
import IncludeTaxToggle from '@/components/IncludeTaxToggle.vue';
import ShowProfitToggle from '@/components/ShowProfitToggle.vue';
import TableCollapseToggle from '@/components/TableCollapseToggle.vue';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import settingx from '@/store/modules/settingx';
import { addComma } from '@/util/number';

@Component({
	components: {
		LineChartFilterable,
		IncludeTaxToggle,
		ShowProfitToggle,
		TableCollapseToggle,
		IncludeCreditNoteToggle,
		IncludeDebitNoteToggle,
		SlPhModuleOnlyToggle,
	},
})
export default class SalesInvoiceTrendLineChart extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['sales'];
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = ['stockItems', 'agents', 'dateRange'];

	public xAxisData: string[] = [];
	public periodTotalData: any[] = [];
	public detailFields: any[] = [];
	public series: echarts.ECharts[] | any = [];

	public get currencySymbol() {
		return gdbx.currencySymbol;
	}

	public get numDecimal() {
		return gdbx.numDecimal;
	}

	public get dateFormatted() {
		return this.selectedDateRange;
	}

	public get includeTax() {
		return settingx.now.includeTax;
	}

	public get showProfit() {
		return settingx.now.showProfit;
	}

	public get includeCreditNote() {
		return settingx.now.includeCreditNote;
	}

	public get includeDebitNote() {
		return settingx.now.includeDebitNote;
	}

	public get slPhModuleOnly() {
		return settingx.now.slPhModuleOnly;
	}

	/**
	 * Key for data fetching - only changes when filters change
	 */
	private get _dataFetchKey(): string {
		return JSON.stringify([
			this.selectedDateRange,
			this.selectedAgents,
			this.selectedStockItems,
		]);
	}

	private _cachedData: {
		slIvCsAmount: number[];
		slIvCsAmountInc: number[];
		slDnAmount: number[];
		slDnAmountInc: number[];
		slIvCsProfit: number[];
		slIvCsProfitInc: number[];
		slDnProfit: number[];
		slDnProfitInc: number[];
		slCnAmount: number[];
		slCnAmountInc: number[];
		slCnProfit: number[];
		slCnProfitInc: number[];
		arIvAmount: number[];
		arDnAmount: number[];
		arCnAmount: number[];
		labels: string[];
	} | null = null;
	private _cachedDataKey: string = '';

	public get color() {
		return this.showProfit
			? [GobiColor.DEEP_TEAL, GobiColor.ORANGE, GobiColor.PINK]
			: [GobiColor.DEEP_TEAL, GobiColor.PINK];
	}

	public get groupedBy() {
		const difference = this.selectedDateRange[1] - this.selectedDateRange[0];
		if (difference >= 365 * 3 * 24 * 60 * 60 * 1000) {
			return 'year';
		}

		if (difference >= 62 * 24 * 60 * 60 * 1000) {
			return 'month';
		}

		// if (difference >= 14 * 24 * 60 * 60 * 1000) {
		// 	return 'week';
		// }

		return 'day';
	}

	public get expensiveHook() {
		const {
			color,
			showProfit,
			includeTax,
			selectedDateRange,
			selectedAgents,
			selectedStockItems,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
		} = this;
		return JSON.stringify([
			color,
			showProfit,
			includeTax,
			selectedDateRange,
			selectedAgents,
			selectedStockItems,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
		]);
	}

	public async expensiveCalc() {
		const jobId = (this.currentJobId = uuid.v4());
		this.$forceUpdate();
		this.generateLoadingText(0.2, 0.3);

		const currentFetchKey = this._dataFetchKey;
		const useCache = this._cachedData !== null && this._cachedDataKey === currentFetchKey;

		let slIvCsAmount: number[];
		let slIvCsAmountInc: number[];
		let slDnAmount: number[];
		let slDnAmountInc: number[];
		let slIvCsProfit: number[];
		let slIvCsProfitInc: number[];
		let slDnProfit: number[];
		let slDnProfitInc: number[];
		let slCnAmount: number[];
		let slCnAmountInc: number[];
		let slCnProfit: number[];
		let slCnProfitInc: number[];
		let arIvAmount: number[];
		let arDnAmount: number[];
		let arCnAmount: number[];
		let labels: string[];

		if (useCache) {
			slIvCsAmount = this._cachedData!.slIvCsAmount;
			slIvCsAmountInc = this._cachedData!.slIvCsAmountInc;
			slDnAmount = this._cachedData!.slDnAmount;
			slDnAmountInc = this._cachedData!.slDnAmountInc;
			slIvCsProfit = this._cachedData!.slIvCsProfit;
			slIvCsProfitInc = this._cachedData!.slIvCsProfitInc;
			slDnProfit = this._cachedData!.slDnProfit;
			slDnProfitInc = this._cachedData!.slDnProfitInc;
			slCnAmount = this._cachedData!.slCnAmount;
			slCnAmountInc = this._cachedData!.slCnAmountInc;
			slCnProfit = this._cachedData!.slCnProfit;
			slCnProfitInc = this._cachedData!.slCnProfitInc;
			arIvAmount = this._cachedData!.arIvAmount;
			arDnAmount = this._cachedData!.arDnAmount;
			arCnAmount = this._cachedData!.arCnAmount;
			labels = this._cachedData!.labels;
		} else {
			this.generateLoadingText(0.3, 0.9);

			const queryHead = await this._ensureTablesLoaded(
				['sales', 'creditNotes', 'customerTrades'] as any[],
			);
			this.checkInterrupted(jobId);

			const salesTable = getTableName('sales');
			const cnTable = getTableName('creditNotes');
			const arTable = getTableName('customerTrades');

			const dateFilter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${this.selectedDateRange[0]} AND ${this.selectedDateRange[1]}`;
			const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
			const allAgentLen = (gdbx.allAgents && gdbx.allAgents.length) || 0;
			const agentFilter = this.selectedAgents && this.selectedAgents.length > 0
				&& this.selectedAgents.length !== allAgentLen
				? ` AND ${COL.AGENT} IN (${this.selectedAgents.map((a: string) => `'${escapeStr(a)}'`).join(', ')})`
				: '';
			const allStockItemsLen = (gdbx.allStockItems && gdbx.allStockItems.length) || 0;
			const stockItemFilter = this.selectedStockItems && this.selectedStockItems.length > 0
				&& this.selectedStockItems.length !== allStockItemsLen
				? ` AND ${COL.ITEMCODE} IN (${this.selectedStockItems.map((s: string) => `'${escapeStr(s)}'`).join(', ')})`
				: '';

			const slFilter = `${dateFilter}${agentFilter}${stockItemFilter}`;
			const arFilter = `${dateFilter}${agentFilter}`;

			const [salesRaw, cnRaw, arRaw] = await Promise.all([
				this._loadSalesDataByPeriod(queryHead, salesTable, slFilter),
				this._loadCreditNotesDataByPeriod(queryHead, cnTable, slFilter),
				this._loadCustomerTradesDataByPeriod(queryHead, arTable, arFilter),
			]);
			this.checkInterrupted(jobId);
			this.generateLoadingText(0.95);

			labels = this._buildPeriodLabels();

			slIvCsAmount = this._alignToPeriodArray(salesRaw, (i) => i.ivCsAmount);
			slIvCsAmountInc = this._alignToPeriodArray(salesRaw, (i) => i.ivCsAmountInc);
			slDnAmount = this._alignToPeriodArray(salesRaw, (i) => i.dnAmount);
			slDnAmountInc = this._alignToPeriodArray(salesRaw, (i) => i.dnAmountInc);
			slIvCsProfit = this._alignToPeriodArray(salesRaw, (i) => i.ivCsProfit);
			slIvCsProfitInc = this._alignToPeriodArray(salesRaw, (i) => i.ivCsProfitInc);
			slDnProfit = this._alignToPeriodArray(salesRaw, (i) => i.dnProfit);
			slDnProfitInc = this._alignToPeriodArray(salesRaw, (i) => i.dnProfitInc);

			slCnAmount = this._alignToPeriodArray(cnRaw, (i) => i.amount);
			slCnAmountInc = this._alignToPeriodArray(cnRaw, (i) => i.amountInc);
			slCnProfit = this._alignToPeriodArray(cnRaw, (i) => i.profit);
			slCnProfitInc = this._alignToPeriodArray(cnRaw, (i) => i.profitInc);

			arIvAmount = this._alignToPeriodArray(arRaw, (i) => i.ivAmount);
			arDnAmount = this._alignToPeriodArray(arRaw, (i) => i.dnAmount);
			arCnAmount = this._alignToPeriodArray(arRaw, (i) => i.cnAmount);

			this._cachedData = {
				slIvCsAmount, slIvCsAmountInc, slDnAmount, slDnAmountInc,
				slIvCsProfit, slIvCsProfitInc, slDnProfit, slDnProfitInc,
				slCnAmount, slCnAmountInc, slCnProfit, slCnProfitInc,
				arIvAmount, arDnAmount, arCnAmount,
				labels,
			};
			this._cachedDataKey = currentFetchKey;
		}

		this.generateLoadingText(1);

		// Calculate based on toggles
		const useInc = this.includeTax;
		let salesData: number[];
		let profitData: number[];
		let creditNotesData: number[];

		if (this.slPhModuleOnly) {
			// SL mode
			const ivCsAmt = useInc ? slIvCsAmountInc : slIvCsAmount;
			const dnAmt = useInc ? slDnAmountInc : slDnAmount;
			const cnAmt = useInc ? slCnAmountInc : slCnAmount;
			const ivCsProf = useInc ? slIvCsProfitInc : slIvCsProfit;
			const dnProf = useInc ? slDnProfitInc : slDnProfit;
			const cnProf = useInc ? slCnProfitInc : slCnProfit;

			salesData = ivCsAmt.map((v, i) =>
				v + (this.includeDebitNote ? dnAmt[i] : 0)
				- (this.includeCreditNote ? cnAmt[i] : 0),
			);
			profitData = ivCsProf.map((v, i) =>
				v + (this.includeDebitNote ? dnProf[i] : 0)
				- (this.includeCreditNote ? cnProf[i] : 0),
			);
			creditNotesData = [...cnAmt];
		} else {
			// AR mode
			salesData = arIvAmount.map((v, i) =>
				v + (this.includeDebitNote ? arDnAmount[i] : 0)
				- (this.includeCreditNote ? arCnAmount[i] : 0),
			);

			// AR profit = SL profit + (AR amount - SL amount)
			const slAmt = useInc ? slIvCsAmountInc : slIvCsAmount;
			const slDnAmt2 = useInc ? slDnAmountInc : slDnAmount;
			const slCnAmt = useInc ? slCnAmountInc : slCnAmount;
			const slProf = useInc ? slIvCsProfitInc : slIvCsProfit;
			const slDnProf = useInc ? slDnProfitInc : slDnProfit;
			const slCnProf = useInc ? slCnProfitInc : slCnProfit;

			profitData = arIvAmount.map((v, i) => {
				const arAmt = v
					+ (this.includeDebitNote ? arDnAmount[i] : 0)
					- (this.includeCreditNote ? arCnAmount[i] : 0);
				const slAmount = slAmt[i]
					+ (this.includeDebitNote ? slDnAmt2[i] : 0)
					- (this.includeCreditNote ? slCnAmt[i] : 0);
				const slProfit = slProf[i]
					+ (this.includeDebitNote ? slDnProf[i] : 0)
					- (this.includeCreditNote ? slCnProf[i] : 0);
				return slProfit + (arAmt - slAmount);
			});

			creditNotesData = [...arCnAmount];
		}

		this.xAxisData = labels;

		const totalSales = salesData.reduce((a, b) => a + b, 0);
		const totalProfit = profitData.reduce((a, b) => a + b, 0);
		const totalCreditNotes = creditNotesData.reduce((a, b) => a + b, 0);

		this.detailFields = [
			{ key: 'total_sales', textAlign: 'center' },
			{ key: 'total_sales_profit', textAlign: 'center' },
		];

		this.periodTotalData = [
			{
				total_sales:
					this.currencySymbol +
					' ' +
					addComma(totalSales, this.numDecimal, true),
				total_sales_profit:
					this.currencySymbol +
					' ' +
					addComma(totalProfit, this.numDecimal, true),
				total_credit_notes:
					this.currencySymbol +
					' ' +
					addComma(totalCreditNotes, this.numDecimal, true),
			},
		];

		if (this.includeCreditNote) {
			this.detailFields.push({
				key: 'total_credit_notes',
				textAlign: 'center',
			});
		}

		const sales = {
			name: 'Sales',
			data: salesData,
			type: 'line',
			areaStyle: {
				color: GobiColor.COLOR1,
				opacity: 1,
			},
			smooth: true,
		};

		const profit = {
			name: 'Profit / Loss',
			data: profitData,
			type: 'line',
			areaStyle: {
				color: GobiColor.COLOR2,
				opacity: 1,
			},
			smooth: true,
		};

		const creditNotes = {
			name: 'Credit Notes',
			data: creditNotesData,
			type: 'line',
			color: GobiColor.COLOR3,
			smooth: true,
		};

		this.series = [sales];

		if (this.showProfit) {
			this.series.push(profit);
		}

		if (this.includeCreditNote) {
			this.series.push(creditNotes);
		}

		this.saveHistory('series', 'xAxisData', 'periodTotalData', 'detailFields');
	}

	private _getPeriodSqlParts(): { select: string; groupBy: string; orderBy: string } {
		const p = this.groupedBy;
		if (p === 'year') {
			return {
				select: sqlExtractYear(COL.DOCDATE),
				groupBy: 'year',
				orderBy: 'year',
			};
		} else if (p === 'month') {
			return {
				select: `${sqlExtractYear(COL.DOCDATE)}, ${sqlExtractMonth(COL.DOCDATE)}`,
				groupBy: 'year, month',
				orderBy: 'year, month',
			};
		} else {
			return {
				select: `${sqlExtractYear(COL.DOCDATE)}, ${sqlExtractMonth(COL.DOCDATE)}, ${sqlExtractDay(COL.DOCDATE)}`,
				groupBy: 'year, month, day',
				orderBy: 'year, month, day',
			};
		}
	}

	private _buildPeriodLabels(): string[] {
		const p = this.groupedBy;
		const start = moment(this.selectedDateRange[0]);
		const end = moment(this.selectedDateRange[1]);
		const labels: string[] = [];

		if (p === 'year') {
			const current = start.clone().startOf('year');
			while (current.isSameOrBefore(end)) {
				labels.push(current.format('YYYY'));
				current.add(1, 'year');
			}
		} else if (p === 'month') {
			const current = start.clone().startOf('month');
			while (current.isSameOrBefore(end)) {
				labels.push(current.format('MMM YY'));
				current.add(1, 'month');
			}
		} else {
			const current = start.clone().startOf('day');
			while (current.isSameOrBefore(end)) {
				labels.push(current.format('DD MMM'));
				current.add(1, 'day');
			}
		}

		return labels;
	}

	private async _loadSalesDataByPeriod(
		queryHead: any, tableName: string, whereClause: string,
	): Promise<Array<{
		year: number; month: number; day: number;
		ivCsAmount: number; ivCsAmountInc: number;
		dnAmount: number; dnAmountInc: number;
		ivCsProfit: number; ivCsProfitInc: number;
		dnProfit: number; dnProfitInc: number;
	}>> {
		const { select, groupBy, orderBy } = this._getPeriodSqlParts();
		const AMT_INC = '(TRY_CAST("AMOUNT" AS DOUBLE) + COALESCE(TRY_CAST("TAX" AS DOUBLE), 0))';
		const PROF_INC = '(TRY_CAST("AMOUNT" AS DOUBLE) + TRY_CAST("TAX" AS DOUBLE) - TRY_CAST("COST" AS DOUBLE))';

		const results = await queryHead.rawQuery(`
			SELECT
				${select},
				${sqlSumByDocType(['IV', 'CS'], COL.AMOUNT, 'iv_cs_amount')},
				SUM(CASE WHEN ${COL.DOCTYPE} IN ('IV','CS') THEN ${AMT_INC} ELSE 0 END) as iv_cs_amount_inc,
				${sqlSumByDocType(['DN'], COL.AMOUNT, 'dn_amount')},
				SUM(CASE WHEN ${COL.DOCTYPE} IN ('DN') THEN ${AMT_INC} ELSE 0 END) as dn_amount_inc,
				${sqlSumByDocType(['IV', 'CS'], COL.PROFIT, 'iv_cs_profit')},
				SUM(CASE WHEN ${COL.DOCTYPE} IN ('IV','CS') THEN ${PROF_INC} ELSE 0 END) as iv_cs_profit_inc,
				${sqlSumByDocType(['DN'], COL.PROFIT, 'dn_profit')},
				SUM(CASE WHEN ${COL.DOCTYPE} IN ('DN') THEN ${PROF_INC} ELSE 0 END) as dn_profit_inc
			FROM ${tableName}
			WHERE ${whereClause}
			GROUP BY ${groupBy}
			ORDER BY ${orderBy}
		`);

		return results.map((r: any) => ({
			year: Number(r.year),
			month: Number(r.month) || 0,
			day: Number(r.day) || 0,
			ivCsAmount: Number(r.iv_cs_amount) || 0,
			ivCsAmountInc: Number(r.iv_cs_amount_inc) || 0,
			dnAmount: Number(r.dn_amount) || 0,
			dnAmountInc: Number(r.dn_amount_inc) || 0,
			ivCsProfit: Number(r.iv_cs_profit) || 0,
			ivCsProfitInc: Number(r.iv_cs_profit_inc) || 0,
			dnProfit: Number(r.dn_profit) || 0,
			dnProfitInc: Number(r.dn_profit_inc) || 0,
		}));
	}

	private async _loadCreditNotesDataByPeriod(
		queryHead: any, tableName: string, whereClause: string,
	): Promise<Array<{
		year: number; month: number; day: number;
		amount: number; amountInc: number;
		profit: number; profitInc: number;
	}>> {
		const { select, groupBy, orderBy } = this._getPeriodSqlParts();
		const AMT_INC = '(TRY_CAST("AMOUNT" AS DOUBLE) + COALESCE(TRY_CAST("TAX" AS DOUBLE), 0))';
		const PROF_INC = '(TRY_CAST("AMOUNT" AS DOUBLE) + TRY_CAST("TAX" AS DOUBLE) - TRY_CAST("COST" AS DOUBLE))';

		const results = await queryHead.rawQuery(`
			SELECT
				${select},
				${sqlSum(COL.AMOUNT, 'amount')},
				SUM(${AMT_INC}) as amount_inc,
				${sqlSum(COL.PROFIT, 'profit')},
				SUM(${PROF_INC}) as profit_inc
			FROM ${tableName}
			WHERE ${whereClause}
			GROUP BY ${groupBy}
			ORDER BY ${orderBy}
		`);

		return results.map((r: any) => ({
			year: Number(r.year),
			month: Number(r.month) || 0,
			day: Number(r.day) || 0,
			amount: Number(r.amount) || 0,
			amountInc: Number(r.amount_inc) || 0,
			profit: Number(r.profit) || 0,
			profitInc: Number(r.profit_inc) || 0,
		}));
	}

	private async _loadCustomerTradesDataByPeriod(
		queryHead: any, tableName: string, whereClause: string,
	): Promise<Array<{
		year: number; month: number; day: number;
		ivAmount: number; dnAmount: number; cnAmount: number;
	}>> {
		const { select, groupBy, orderBy } = this._getPeriodSqlParts();

		const results = await queryHead.rawQuery(`
			SELECT
				${select},
				${sqlSumByDocType(['IV'], COL.LOCALAMOUNT, 'iv_amount')},
				${sqlSumByDocType(['DN'], COL.LOCALAMOUNT, 'dn_amount')},
				${sqlSumByDocType(['CN'], COL.LOCALAMOUNT, 'cn_amount')}
			FROM ${tableName}
			WHERE ${whereClause}
			GROUP BY ${groupBy}
			ORDER BY ${orderBy}
		`);

		return results.map((r: any) => ({
			year: Number(r.year),
			month: Number(r.month) || 0,
			day: Number(r.day) || 0,
			ivAmount: Number(r.iv_amount) || 0,
			dnAmount: Number(r.dn_amount) || 0,
			cnAmount: Number(r.cn_amount) || 0,
		}));
	}

	private _alignToPeriodArray<T extends { year: number; month: number; day: number }>(
		data: T[],
		getValue: (item: T) => number,
	): number[] {
		const p = this.groupedBy;
		const start = moment(this.selectedDateRange[0]);
		const end = moment(this.selectedDateRange[1]);

		const indexMap = new Map<string, number>();
		const current = start.clone();
		let pos = 0;

		if (p === 'year') {
			current.startOf('year');
			while (current.isSameOrBefore(end)) {
				indexMap.set(`${current.year()}`, pos++);
				current.add(1, 'year');
			}
		} else if (p === 'month') {
			current.startOf('month');
			while (current.isSameOrBefore(end)) {
				indexMap.set(`${current.year()}-${current.month() + 1}`, pos++);
				current.add(1, 'month');
			}
		} else {
			current.startOf('day');
			while (current.isSameOrBefore(end)) {
				indexMap.set(`${current.year()}-${current.month() + 1}-${current.date()}`, pos++);
				current.add(1, 'day');
			}
		}

		const result = Array(pos).fill(0);

		for (const item of data) {
			let key: string;
			if (p === 'year') {
				key = `${item.year}`;
			} else if (p === 'month') {
				key = `${item.year}-${item.month || 1}`;
			} else {
				key = `${item.year}-${item.month || 1}-${item.day || 1}`;
			}
			const idx = indexMap.get(key);
			if (idx !== undefined) {
				result[idx] = getValue(item);
			}
		}

		return result;
	}
}
